import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@dreamtree-org/twreact-ui';
import {
  LayoutDashboard, Receipt, Package, Users, Settings, Shield, KeySquare, Laptop, Store, Circle,
  Plus, Upload, Tag, Wallet, Palette, Bot, Truck, Share2, Handshake,
} from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useAuth, useCan } from '@rbac/useCan';
import { clearAuth } from '@rbac/permissions';
import { signOutThroughDreamtree } from '@/lib/dreamtreeSso';
import { buildRoutes } from '@/routes';
import { useModules } from '@/modules';
import { NAV_GROUPS } from '@/merchant/routes';
import { useStoreLogo } from '@/lib/storeLogo';
import { StaffShell } from '@/components';

const ICONS = {
  'layout-dashboard': LayoutDashboard, receipt: Receipt, package: Package, laptop: Laptop, users: Users, settings: Settings,
  shield: Shield, 'key-square': KeySquare, plus: Plus, upload: Upload, tag: Tag, wallet: Wallet, palette: Palette, bot: Bot, truck: Truck, 'share-2': Share2, handshake: Handshake,
};
const Icon = ({ name, size = 18 }) => {
  const C = ICONS[name] || Circle;
  return <C size={size} aria-hidden />;
};

/** Is `path` the current screen (or its parent, e.g. /orders for /orders/MP1)? */
export function isActivePath(path, pathname) {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Phone tab bar: the `foot` routes with "Add listing" as the centre action when the user may add stock. */
export function footNavItems(allowed, activePath) {
  const tab = (r) => ({ id: r.path, label: r.short || r.label.split(' ')[0], to: r.path, icon: <Icon name={r.icon} size={20} />, active: !!activePath && activePath.path === r.path });
  const tabs = allowed.filter((r) => r.foot).slice(0, 4).map(tab);
  const add = allowed.find((r) => r.path === '/add');
  if (add) tabs.splice(Math.min(2, tabs.length), 0, { ...tab(add), label: 'Add', variant: 'action', 'aria-label': 'Add listing' });
  return tabs;
}

/**
 * Back-office shell (StaffShell / twreact AppShell): sidebar grouped Overview / Catalog / Sales /
 * Finance / Settings / Data from lg, drawer below, FootNav on phones. Nav items are filtered by
 * the user's (module, action) permissions.
 */
export default function DefaultLayout({ store }) {
  const nav = useNavigate();
  const loc = useLocation();
  const user = useAuth();
  const can = useCan();
  const { fetchApi } = useAppApi();

  // A route with no module is open to every signed-in staff member (e.g. AI assistants, D48).
  const allowed = buildRoutes(useModules(), { store }).filter((r) => r.nav !== false && (!r.module || can(r.module, r.action)));
  const order = (g) => {
    const i = NAV_GROUPS.indexOf(g);
    return i === -1 ? NAV_GROUPS.length : i;
  };
  // The longest matching path wins, so /inventory/new highlights "Add unit", not "Inventory".
  const activePath = allowed.filter((r) => isActivePath(r.path, loc.pathname)).sort((a, b) => b.path.length - a.path.length)[0];

  const sidebarItems = [...allowed]
    .sort((a, b) => order(a.group) - order(b.group))
    .map((r) => ({ id: r.path, label: r.label, href: r.path, icon: <Icon name={r.icon} />, group: r.group, active: !!activePath && activePath.path === r.path }));
  const storeName = (store && store.name) || 'MerchantPro';
  const logo = useStoreLogo();

  const logout = async () => {
    // A Dreamtree ID session (DTID-44) signs out at the IdP too: the server ends this
    // session, then redirects to Dreamtree ID's end-session endpoint.
    if (user && user.sso) {
      clearAuth();
      signOutThroughDreamtree();
      return;
    }
    try { await fetchApi('logout'); } catch {}
    clearAuth();
    nav('/login', { replace: true });
  };

  return (
    <StaffShell
      brand={
        <span className="flex items-center gap-2 min-w-0">
          {logo ? <img src={logo.url} alt="" className="h-8 w-auto max-w-28 shrink-0 rounded-control bg-surface object-contain p-0.5" /> : null}
          <span className="min-w-0">
            <span className="block font-semibold truncate">{storeName}</span>
            <span className="block text-micro opacity-70">Store admin</span>
          </span>
        </span>
      }
      pageTitle={activePath ? activePath.label : storeName}
      nav={sidebarItems}
      foot={footNavItems(allowed, activePath)}
      user={user}
      onLogout={logout}
      actions={
        // The storefront lives outside the /admin router basename — a full page load.
        <Button as="a" href="/" variant="ghost" size="icon" aria-label="View storefront" title="View storefront">
          <Store size={18} aria-hidden />
        </Button>
      }
    >
      <Outlet />
    </StaffShell>
  );
}
